import { errorView, isTransportError } from '@krp/client-runtime'

import type { Cashier } from '../../agent-session/model/agentSessionController'

/**
 * Кассир РМК «Собственной» (RP-OWN11, часть Б): чистые правила без React и сети. Кассира выбирают из списка службы Cashiers и
 * подтверждают кодом; из ответа берутся `sourceCashierId` (GUID узла `cashier` структуры Receipts → `cashierId` агента) и имя. код
 * существует только в теле одного запроса; grant живёт в памяти окна (срок жизни `screen`).
 */

const GUID_D = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** GUID формата D в нижнем регистре или `null`: чужая форма (скобки, без дефисов, имя) в `cashierId` не уходит. */
export function normalizeCashierGuid(raw: unknown): string | null {
  return typeof raw === 'string' && GUID_D.test(raw) ? raw.toLowerCase() : null
}

/** Кассир из списка: можно выбрать. */
export interface PosCashierChoice {
  /** Кассир нашей системы: ему принадлежит код. */
  cashierId: string
  /** GUID узла `cashier` структуры Receipts — кассир этой кассы, связанный с кассиром нашей системы. */
  sourceCashierId: string
  name: string
}

/** Билет входа и рабочие места, где кассир может работать (ответ `people/sign-in`). Билет нужен только для следующего запроса. */
export interface PosSignIn {
  ticket: string
  workplaces: readonly { resourceId: string; sourceCashierId: string }[]
}

/** Подтверждение кассира по коду (`purpose = pos`) как отвечает служба. Токен `grant` дальше границы вызова не идёт. */
export interface PosCashierGrant {
  /** GUID узла `cashier` (формат D, нижний регистр) — идёт в `cashierId` чека, смены, наличных и X-отчёта. */
  cashierId: string
  name: string
  grant: string
  /** Unix-время (мс), после которого подтверждение не действует. */
  expiresAtMs: number
}

const record = (raw: unknown, what: string): Record<string, unknown> => {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) throw new Error(`${what}: ожидался объект`)
  return raw as Record<string, unknown>
}

const filled = (value: unknown): string | null => (typeof value === 'string' && value.trim() !== '' ? value : null)

/** Ответ `GET people/workplace`: без GUID кассира источника пропускаем — `cashierId` агента из такого не получится. */
export function decodeCashierChoices(raw: unknown): readonly PosCashierChoice[] {
  const items = record(raw, 'Список кассиров').items
  if (!Array.isArray(items)) throw new Error('Список кассиров: нет поля items')
  const result: PosCashierChoice[] = []
  for (const item of items as unknown[]) {
    const value = record(item, 'Кассир')
    const cashierId = filled(value.cashierId)
    const sourceCashierId = normalizeCashierGuid(value.sourceCashierId)
    const name = filled(value.displayName)
    if (cashierId === null || sourceCashierId === null || name === null) continue
    result.push({ cashierId, sourceCashierId, name })
  }
  return result
}

/** Ответ `POST people/sign-in`. */
export function decodeSignIn(raw: unknown): PosSignIn {
  const value = record(raw, 'Вход кассира')
  const ticket = filled(value.ticket)
  if (ticket === null || !Array.isArray(value.workplaces)) throw new Error('Служба кассиров вернула неполный ответ входа.')
  const workplaces = (value.workplaces as unknown[]).map((entry) => {
    const place = record(entry, 'Рабочее место')
    const resourceId = filled(place.resourceId)
    const sourceCashierId = filled(place.sourceCashierId)
    if (resourceId === null || sourceCashierId === null) throw new Error('Служба кассиров вернула неполное рабочее место.')
    return { resourceId, sourceCashierId }
  })
  return { ticket, workplaces }
}

/** Ответ `POST /verify`. Идентификатор не в формате GUID — ошибка, а не догадка: иначе чек уйдёт без кассира. */
export function decodePosGrant(raw: unknown): PosCashierGrant {
  const value = record(raw, 'Подтверждение кассира')
  const cashierId = normalizeCashierGuid(value.sourceCashierId)
  if (cashierId === null) throw new Error('Служба кассиров вернула идентификатор кассира не в формате GUID.')
  const grant = filled(value.grant)
  const name = filled(value.cashierName)
  const expiresAtMs = typeof value.expiresAt === 'string' ? Date.parse(value.expiresAt) : Number.NaN
  if (grant === null || name === null || !Number.isFinite(expiresAtMs)) throw new Error('Служба кассиров вернула неполное подтверждение кассира.')
  return { cashierId, name, grant, expiresAtMs }
}

/**
 * Подтверждение, как его помнит окно: кто и до какого времени. Токен `grant` и код в состояние не попадают: для запросов агента
 * нужны только GUID и имя, а срок — чтобы вовремя потребовать код ещё раз.
 */
export type CashierConfirmation = Pick<PosCashierGrant, 'cashierId' | 'name' | 'expiresAtMs'>

export const confirmationOf = (grant: PosCashierGrant): CashierConfirmation => ({ cashierId: grant.cashierId, name: grant.name, expiresAtMs: grant.expiresAtMs })

/** Кассир для запросов агента: имя и `cashierId` из подтверждения. */
export const cashierOfGrant = (grant: CashierConfirmation): Cashier => ({ name: grant.name, cashierId: grant.cashierId })

/** Ошибка службы Cashiers для показа: текст сервера дословно, код и номер запроса — для передачи администратору. */
export interface CashierProblem {
  text: string
  code: string | null
  correlationId: string | null
  /** Всё вместе, одним текстом для «Скопировать». */
  report: string
}

export function problemOf(error: unknown, what: string): CashierProblem {
  const view = errorView(error)
  const code = view.code === 'client' ? null : view.code
  const correlationId = view.correlationId === null || view.correlationId === '' ? null : view.correlationId
  const retry = isTransportError(error) ? error.details?.retryAfterSeconds : undefined
  const lines = [
    `Что делали: ${what}`,
    `Текст: ${view.message}`,
    ...(code === null ? [] : [`Код: ${code}`]),
    ...(correlationId === null ? [] : [`Номер запроса: ${correlationId}`]),
    ...(typeof retry === 'number' ? [`Повторить можно через ${retry} с`] : []),
  ]
  return { text: view.message, code, correlationId, report: lines.join('\n') }
}

export const CART_ALLOWED = 'Набирать корзину можно.'
export const NO_CASHIERS_TEXT = 'К этой кассе не привязан ни один кассир: администратор связывает кассиров нашей системы с кассирами кассы в разделе «Кассиры». Пока кассира нет, оплата, смена, наличные и X-отчёт закрыты.'

export type CashierListState =
  | { status: 'loading' }
  | { status: 'ready'; items: readonly PosCashierChoice[] }
  | { status: 'error'; problem: CashierProblem }

/**
 * Почему деньги (оплата, смена, наличные, X-отчёт) сейчас закрыты; `null` — кассир подтверждён. Набор корзины этим не закрыт.
 * Правило допуска живёт здесь, в слое РМК, а не в пороге агента (`sellGate`).
 */
export function cashierBlockReason(input: { list: CashierListState; selected: PosCashierChoice | null; grant: CashierConfirmation | null; expired: boolean }): string | null {
  if (input.grant !== null) return null
  if (input.list.status === 'loading') return 'Читаем список кассиров… Пока он не получен, оплата, смена, наличные и X-отчёт закрыты.'
  if (input.list.status === 'error') return 'Список кассиров не получен: оплата, смена, наличные и X-отчёт закрыты. Нажмите «Прочитать заново».'
  if (input.list.items.length === 0) return NO_CASHIERS_TEXT
  if (input.selected === null) return `Выберите кассира и введите его код: без этого оплата, смена, наличные и X-отчёт закрыты. ${CART_ALLOWED}`
  if (input.expired) return `Подтверждение кассира «${input.selected.name}» истекло: введите код ещё раз. ${CART_ALLOWED}`
  return `Введите код кассира «${input.selected.name}»: без этого оплата, смена, наличные и X-отчёт закрыты. ${CART_ALLOWED}`
}
